"use client";

import { useState, useEffect } from "react";
import { ContactRound, Download, Loader2, CheckCircle } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Badge } from "@/app/_module/components/ui/badge";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useUserPhoneNumbers } from "@/app/_module/hooks/usePhoneNumberPricing";

import { useContactCard, useSaveContactCard, type ContactCardData } from "../hooks/contact-card-queries";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

function formatPhoneNumber(num: string): string {
  const cleaned = num.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return num;
}

function generateVcf(data: ContactCardData, formatPhone = false): string {
  const phoneDisplay = formatPhone ? formatPhoneNumber(data.phone) : data.phone;
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${data.firstName} ${data.lastName}`,
    `N:${data.lastName};${data.firstName};;;`,
  ];
  if (data.company) lines.push(`ORG:${data.company}`);
  if (data.title) lines.push(`TITLE:${data.title}`);
  lines.push(`TEL;TYPE=CELL:${phoneDisplay}`);
  if (data.email) lines.push(`EMAIL:${data.email}`);
  if (data.website) lines.push(`URL:${data.website}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}

async function uploadVcfFile(vcfContent: string, firstName: string, lastName: string): Promise<string> {
  const blob = new Blob([vcfContent], { type: "text/vcard" });
  const file = new File([blob], `${firstName}_${lastName}_contact.vcf`, { type: "text/vcard" });

  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post<any>({
    url: getApiUrl("communication/sms/upload-media"),
    body: formData,
    auth: true,
  });

  console.log("Upload VCF response:", JSON.stringify(response?.data ?? response));
  // response.data is the axios wrapper, inner .data is the backend payload
  const url = response?.data?.data?.url || response?.data?.url || (response as any)?.data?.publicUrl;
  if (!url) {
    throw new Error("No URL returned from upload");
  }
  return url;
}

export function ContactCardSettings() {
  const { user } = useUserContext();
  const { data: phoneNumbers = [] } = useUserPhoneNumbers();
  const { data: contactCardData, isLoading } = useContactCard();
  const saveContactCard = useSaveContactCard();
  const { toast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("Insurance Agent");
  const [website, setWebsite] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const [hasInitialized, setHasInitialized] = useState(false);

  // Load existing card data or auto-fill from profile
  useEffect(() => {
    if (hasInitialized || isLoading) return;

    if (contactCardData?.contactCardData) {
      const d = contactCardData.contactCardData;
      setFirstName(d.firstName || "");
      setLastName(d.lastName || "");
      setPhone(d.phone || "");
      setEmail(d.email || "");
      setCompany(d.company || "");
      setTitle(d.title || "Insurance Agent");
      setWebsite(d.website || "");
    } else if (user) {
      // Auto-fill from user profile
      const nameParts = (user.user?.name || "").split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setEmail(user.user?.email || "");
      setCompany(user.currentAgency?.agency?.name || "");
      // Pre-select first phone number
      if (phoneNumbers.length > 0) {
        const firstPhone = phoneNumbers[0]?.number || phoneNumbers[0]?.phoneNumber || "";
        setPhone(firstPhone);
      }
    }
    setHasInitialized(true);
  }, [contactCardData, isLoading, user, phoneNumbers, hasInitialized]);

  const getPhoneNumber = (p: any): string => p.number || p.phoneNumber || "";

  const formData: ContactCardData = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    phone,
    email: email.trim() || undefined,
    company: company.trim() || undefined,
    title: title.trim() || undefined,
    website: website.trim() || undefined,
  };

  const isValid = formData.firstName && formData.lastName && formData.phone;
  const vcfPreview = isValid ? generateVcf(formData, true) : "";
  const hasExistingCard = !!contactCardData?.contactCardVcfUrl;

  const handleGenerate = async () => {
    if (!isValid) return;
    setIsGenerating(true);
    try {
      const vcfContent = generateVcf(formData);
      const vcfUrl = await uploadVcfFile(vcfContent, formData.firstName, formData.lastName);
      await saveContactCard.mutateAsync({ contactCardData: formData, vcfUrl });
    } catch (err) {
      console.error("Contact card generation failed:", err);
      toast({
        title: "Generation Failed",
        description: err instanceof Error ? err.message : "Failed to generate contact card. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!isValid) return;
    const vcfContent = generateVcf(formData);
    const blob = new Blob([vcfContent], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${formData.firstName}_${formData.lastName}_contact.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ContactRound className="h-5 w-5" />
            Contact Card Generator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ContactRound className="h-5 w-5" />
          Contact Card Generator
          {hasExistingCard && (
            <Badge variant="default" className="bg-green-600 ml-2">
              Card Generated
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Generate a vCard (.vcf) contact card to send to leads via MMS.
          When they save it, your calls show your name instead of &quot;Spam Likely&quot; — bypassing iOS 26 and Android call screening.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cc-firstName">First Name *</Label>
            <Input
              id="cc-firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Chris"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-lastName">Last Name *</Label>
            <Input
              id="cc-lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-phone">Phone Number *</Label>
            <Select value={phone} onValueChange={setPhone}>
              <SelectTrigger id="cc-phone">
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {phoneNumbers.map((p: any) => {
                  const num = getPhoneNumber(p);
                  return (
                    <SelectItem key={p.id || num} value={num}>
                      {formatPhoneNumber(num)}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Use the same number your dialer calls from.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-email">Email</Label>
            <Input
              id="cc-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="chris@agency.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-company">Company / Agency</Label>
            <Input
              id="cc-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="ABC Insurance Agency"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-title">Job Title</Label>
            <Input
              id="cc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Insurance Agent"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="cc-website">Website</Label>
            <Input
              id="cc-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://myagency.com"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleGenerate}
            disabled={!isValid || isGenerating}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : hasExistingCard ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Regenerate Card
              </>
            ) : (
              <>
                <ContactRound className="h-4 w-4 mr-2" />
                Generate & Save
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={!isValid}
          >
            <Download className="h-4 w-4 mr-2" />
            Download .vcf
          </Button>
        </div>

        {/* Live Preview */}
        {isValid && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Phone-style preview */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Card Preview</Label>
              <div className="bg-gray-50 border rounded-xl p-4 max-w-[280px]">
                <div className="bg-white rounded-lg shadow-sm border p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-lg">
                      {firstName.charAt(0)}{lastName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{firstName} {lastName}</p>
                      {title && <p className="text-xs text-muted-foreground">{title}</p>}
                    </div>
                  </div>
                  {company && (
                    <p className="text-xs text-muted-foreground border-t pt-2">{company}</p>
                  )}
                  <div className="space-y-1 border-t pt-2">
                    <p className="text-xs"><span className="text-muted-foreground">Phone:</span> {formatPhoneNumber(phone)}</p>
                    {email && <p className="text-xs"><span className="text-muted-foreground">Email:</span> {email}</p>}
                    {website && <p className="text-xs"><span className="text-muted-foreground">Web:</span> {website}</p>}
                  </div>
                </div>
              </div>
            </div>
            {/* VCF content */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">VCF File Content</Label>
              <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-4 overflow-x-auto font-mono whitespace-pre-wrap">
                {vcfPreview}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
